const STRIPE_TAX_TYPE_NAMES: Readonly<Record<string, string>> = {
  sales_tax: 'Sales tax',
  vat: 'VAT',
  gst: 'GST',
  hst: 'HST',
  pst: 'PST',
  qst: 'QST',
  rst: 'RST',
  jct: 'JCT',
  igst: 'IGST',
  lease_tax: 'Lease tax',
  communications_tax: 'Communications tax',
  amusement_tax: 'Amusement tax',
};

export function getTaxDisplayName(taxType?: string | null): string {
  return (taxType && STRIPE_TAX_TYPE_NAMES[taxType]) || 'Tax';
}

export type TaxLineLabelInput = {
  name?: string | null;
  percentage?: number | string | null;
  inclusive?: boolean;
  /** "GB" or "US, CA": the invoice PDF and the dashboard name it, the checkout and emails do not. */
  region?: string | null;
  /** The checkout passes its theme label; everywhere else reads "Includes". */
  includedText?: string;
};

/**
 * One tax line, worded the same everywhere: "Includes VAT (20%)" when the price
 * includes it, "VAT (20%)" when it is added on top. Never an "on $X" base: for an
 * included rate that figure appears nowhere else on the page.
 */
export function formatTaxLineLabel({
  name,
  percentage,
  inclusive = false,
  region,
  includedText = 'Includes',
}: TaxLineLabelInput): string {
  const rate = percentage == null || percentage === '' ? null : `${Number(percentage)}%`;
  const detail = [region, rate].filter(Boolean).join(' ');
  const label = `${name || 'Tax'}${detail ? ` (${detail})` : ''}`;
  return inclusive ? `${includedText} ${label}` : label;
}

type SettledTaxRecord = {
  amount?: number | null;
  taxAmount?: number | null;
  taxSource?: string | null;
  taxRates?: ({ inclusive?: boolean | null } | null)[] | null;
  processingFeeContribution?: { amount?: number | null } | null;
};

/**
 * Whether the tax sat inside the prices, from the record alone (no record stores
 * it for Stripe Tax): a fixed rate says so; otherwise a pre-tax total equal to
 * the amount charged means included, equal to the amount less the tax means on top.
 * The processing fee contribution sits on top of the tax, so it comes off first.
 */
export function getSettledTaxBehavior(
  record: SettledTaxRecord,
  pretaxTotal: number,
): 'inclusive' | 'exclusive' | undefined {
  if (!record.taxAmount) return undefined;
  if (record.taxSource === 'fixed_tax_rate') {
    const inclusive = record.taxRates?.find(
      rate => typeof rate?.inclusive === 'boolean',
    )?.inclusive;
    if (typeof inclusive === 'boolean') return inclusive ? 'inclusive' : 'exclusive';
  }
  const amount =
    record.amount == null
      ? record.amount
      : record.amount - (record.processingFeeContribution?.amount ?? 0);
  if (pretaxTotal === amount) return 'inclusive';
  if (pretaxTotal === (amount ?? 0) - record.taxAmount) return 'exclusive';
  return undefined;
}
